import { v4 as uuidv4 } from "uuid";

export function supportIdentity(user) {
  const account = user?.account;
  return account?.homeAccountId || [account?.tenantId, account?.localAccountId].filter(Boolean).join(":") || `${user?.provider || ""}:${user?.email || ""}`;
}

export function newComposer() { return { text: "", pending: null, conversationId: null, draftConversationId: null, attachments: [] }; }
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const key = scope => `fieldprint.supportComposer.v1:${encodeURIComponent(scope)}`;

// Only bounded metadata is recoverable. File bytes and object URLs never enter storage.
function recoverAttachments(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 5).filter(file => uuid.test(file?.id || "") && typeof file.filename === "string" && file.filename.length > 0 && file.filename.length <= 255 && Number.isInteger(file.size_bytes) && file.size_bytes > 0 && file.size_bytes <= 10485760).map(file => ({
    id: file.id, filename: file.filename, size_bytes: file.size_bytes,
    content_type: typeof file.content_type === "string" ? file.content_type.slice(0, 100) : "application/octet-stream",
    stagedAt: Number.isFinite(file.stagedAt) ? file.stagedAt : 0,
    renewId: file.renewId === true || Boolean(file.stagedAt && Date.now() - file.stagedAt >= 86400000),
    status: file.status === "ready" && Date.now() - file.stagedAt < 86400000 ? "ready" : "reselect",
  }));
}

export function readComposer(scope) {
  try {
    const value = JSON.parse(localStorage.getItem(key(scope)));
    if (!value || Date.now() - value.savedAt > 7 * 86400000 || typeof value.text !== "string" || value.text.length > 4000) return newComposer();
    const conversationId = uuid.test(value.conversationId || "") ? value.conversationId : null;
    const draftConversationId = uuid.test(value.draftConversationId || "") ? value.draftConversationId : null;
    const attachments = recoverAttachments(value.attachments);
    const candidate = value.pending;
    const attachmentIds = Array.isArray(candidate?.attachment_ids) ? candidate.attachment_ids : [];
    const validIds = (candidate?.attachment_ids === undefined || Array.isArray(candidate.attachment_ids)) && attachmentIds.length <= 5 && attachmentIds.every(id => uuid.test(id)) && new Set(attachmentIds).size === attachmentIds.length;
    const valid = candidate && uuid.test(candidate.conversationId || "") && uuid.test(candidate.client_message_id || "") && typeof candidate.newConversation === "boolean" && typeof candidate.text === "string" && (candidate.text.trim() || attachmentIds.length) && candidate.text.length <= 4000 && validIds;
    let pending = null;
    if (valid) {
      const context = candidate.context;
      const safeContext = context && ["dashboard", "farm", "field", "cropyear", "excel", "support", "other"].includes(context.page) && /^[a-zA-Z0-9._-]{1,64}$/.test(context.client_release || "") && Array.isArray(context.request_ids) && context.request_ids.length <= 8 && context.request_ids.every(id => uuid.test(id));
      pending = { conversationId: candidate.conversationId, client_message_id: candidate.client_message_id, newConversation: candidate.newConversation, text: candidate.text, context: safeContext ? { page: context.page, client_release: context.client_release, request_ids: context.request_ids } : null, ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}) };
    }
    return { text: value.text, conversationId, draftConversationId, attachments, pending, conversationLimit: Boolean(value.conversationLimit && !conversationId && draftConversationId), recoveryWarning: Boolean(candidate && !valid) || Boolean(value.conversationId && !conversationId) || (Array.isArray(value.attachments) && attachments.length !== value.attachments.length) };
  } catch { return newComposer(); }
}

export function storeComposer(scope, composer) {
  try { localStorage.setItem(key(scope), JSON.stringify({ ...composer, attachments: (composer.attachments || []).map(({ id, filename, size_bytes, content_type, stagedAt, status, renewId }) => ({ id, filename, size_bytes, content_type, stagedAt, status, renewId })), savedAt: Date.now() })); return true; }
  catch { return false; }
}

export function prepareMessage(composer, context) {
  return composer.pending || {
    conversationId: composer.conversationId || composer.draftConversationId || uuidv4(),
    newConversation: !composer.conversationId,
    client_message_id: uuidv4(),
    text: composer.text,
    context,
    ...((composer.attachments || []).length ? { attachment_ids: composer.attachments.map(file => file.id) } : {}),
  };
}

export function coarseSupportPage(path) {
  if (/\/cropyear(?:\/|$)/.test(path)) return "cropyear";
  if (/\/field(?:\/|$)/.test(path)) return "field";
  if (/\/farm(?:\/|$)/.test(path)) return "farm";
  if (/\/excel(?:\/|$)/.test(path)) return "excel";
  if (/\/support(?:\/|$)/.test(path)) return "support";
  if (/\/dashboard(?:\/|$)/.test(path)) return "dashboard";
  return "other";
}
